import { Link, useNavigate } from "@tanstack/react-router";
import { Button } from "../ui/button";
import AccountBox from "@material-design-icons/svg/outlined/account_box.svg?react";
import Login from "@material-design-icons/svg/outlined/login.svg?react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { PinPlusIcon } from "~/icons/PinPlusIcon";
import { useAuthentication, authClient } from "~/lib/better-auth/client";
import { useQueryClient } from "@tanstack/react-query";
import { MenuIcon, X } from "lucide-react";
import { UserIcon } from "~/icons/UserIcon";
import { BookmarkHeartIcon } from "~/icons/BookmarkHeartIcon";
import { SendArrowDownIcon } from "~/icons/SendArrowDownIcon";
import { ChevronDownIcon } from "~/icons/ChevronDownIcon";
import { PinIcon } from "~/icons/PinIcon";
import { cn } from "~/lib/utils";
import { HeartFilledIcon } from "~/icons/HeartFilledIcon";
import { openInquiry } from "~/lib/stores/inquiry";
import { withLocale } from "~/lib/utils/with-locale";

export const TopPanel = ({
  layoutWide = false,
  menuIsOpen,
  onMenuToggle,
}: {
  layoutWide?: boolean;
  menuIsOpen: boolean;
  onMenuToggle: (isOpen: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { userSession } = useAuthentication();

  return (
    <>
      <header className="bg-black text-white h-(--top-panel-height) relative z-30">
        <div className="layout-content-grid max-w-(--layout-page-max-width) mx-auto h-(--top-panel-height)">
          <div
            className={cn(
              "flex items-center justify-between h-(--top-panel-height)",
              layoutWide
                ? "layout-place-full-width px-(--layout-side-padding)"
                : "layout-place-outer-wrap",
            )}
          >
            <Link to={withLocale("/")} className="shrink-0">
              <img
                src="/logo.png"
                alt="logo"
                width={185}
                height={40}
                className="h-10 w-auto"
              />
            </Link>

            <div className="flex md:hidden">
              <Button
                size={"icon"}
                variant={"ghost"}
                onClick={() => onMenuToggle(!menuIsOpen)}
              >
                <div className="sr-only">Menü</div>
                {menuIsOpen ? (
                  <X className="size-7" />
                ) : (
                  <MenuIcon className="size-10" />
                )}
              </Button>
            </div>
            <div className="hidden md:flex gap-12 items-center">
              <div className="flex items-center gap-4">
                {userSession ? (
                  <Button
                    size={"icon"}
                    variant={"ghost"}
                    asChild
                    className="hover:bg-transparent hover:scale-120 transition-transform duration-300"
                  >
                    <Link to="/dashboard/watchlist" title="Zur Merkliste">
                      <HeartFilledIcon className="size-5 text-primary" />
                    </Link>
                  </Button>
                ) : (
                  <Button
                    size={"icon"}
                    variant={"ghost"}
                    title="Zur Merkliste"
                    className="hover:bg-transparent hover:scale-120 transition-transform duration-300"
                    onClick={() => openInquiry("watchlist", [])}
                  >
                    <HeartFilledIcon className="size-5 text-primary" />
                  </Button>
                )}
                {/* <DropdownMenu>
                  <DropdownMenuTrigger>test</DropdownMenuTrigger>
                  <DropdownMenuContent></DropdownMenuContent>
                </DropdownMenu> */}
                {userSession ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger className="py-1 px-2 border-dashed border-white/40 rounded-md focus-visible:border-2 outline-0">
                      <span className="text-white flex fill-current gap-2 cursor-pointer">
                        Hi, {userSession.user.firstName}!
                        <AccountBox className="size-6" />
                        <ChevronDownIcon className="size-6" />
                      </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      variant="dark"
                      className="translate-y-5 -translate-x-[21px]"
                      align="start"
                    >
                      <DropdownMenuItem
                        className="flex gap-4 px-4 py-3"
                        asChild
                      >
                        <Link to="/dashboard/user">
                          <UserIcon className="fill-current text-white size-6" />
                          <span>
                            <span className="block font-medium">
                              Benutzerkonto
                            </span>
                            <span className="block font-light">
                              Benutzerkonto verwalten
                            </span>
                          </span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="flex gap-4 px-4 py-3"
                        asChild
                      >
                        <Link to="/dashboard/watchlist">
                          <BookmarkHeartIcon className="fill-current text-white size-6" />
                          <span>
                            <span className="block font-medium">Merkliste</span>
                            <span className="block font-light">
                              Zu meiner Merkliste
                            </span>
                          </span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="flex gap-4 px-4 py-3"
                        asChild
                      >
                        <Link to="/dashboard/inquiries">
                          <SendArrowDownIcon className="fill-current text-white size-6" />
                          <span>
                            <span className="block font-medium">Anfragen</span>
                            <span className="block font-light">
                              Zu meinen Anfragen
                            </span>
                          </span>
                        </Link>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        className="flex gap-4 px-4 py-3"
                        onClick={async () => {
                          await authClient.signOut();
                          queryClient.invalidateQueries({
                            queryKey: ["auth", "user"],
                          });
                          navigate({ to: withLocale("/sign-in") });
                        }}
                      >
                        <Login className="fill-current text-white size-6" />
                        <span>
                          <span className="block font-medium">Logout</span>
                          <span className="block font-light">
                            Aus diesem Account abmelden
                          </span>
                        </span>
                      </DropdownMenuItem>

                      {userSession?.user?.isAdvertiser && (
                        <DropdownMenuItem
                          className="flex gap-4 px-4 py-3"
                          asChild
                        >
                          <a
                            href={`${import.meta.env.VITE_ADMIN_URL}${withLocale("/locations")}`}
                          >
                            <PinIcon className="fill-current text-white size-6" />
                            <span>
                              <span className="block font-medium">
                                Locations verwalten
                              </span>
                              <span className="block font-light">
                                Zu meinem Vermieterbereich
                              </span>
                            </span>
                          </a>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger>
                      <span className="text-white flex fill-current gap-2 cursor-pointer">
                        <AccountBox className="size-6" />
                        Einloggen
                      </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      variant="dark"
                      className="translate-y-5 -translate-x-[21px]"
                      align="start"
                    >
                      <DropdownMenuItem
                        className="flex gap-4 px-4 py-3"
                        asChild
                      >
                        <Link to={withLocale("/sign-in")}>
                          <Login className="fill-current text-white size-6" />
                          <span>
                            <span className="block font-medium">Login</span>
                            <span className="block font-light">
                              Für Nutzer, die Locations suchen
                            </span>
                          </span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="flex gap-4 px-4 py-3"
                        asChild
                      >
                        <a
                          href={`${import.meta.env.VITE_ADMIN_URL}${withLocale("/sign-in")}`}
                        >
                          <Login className="fill-current text-white size-6" />
                          <span>
                            <span className="block font-medium">
                              Partner-Login
                            </span>
                            <span className="block font-light">
                              Für Betreiber, die Locations verwalten
                            </span>
                          </span>
                        </a>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              {!userSession ? (
                <Button asChild>
                  <a
                    href={`${import.meta.env.VITE_ADMIN_URL}${withLocale("/sign-up")}`}
                    target="_blank"
                  >
                    <PinPlusIcon className="size-5" />
                    Location eintragen
                  </a>
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
